import { createContext, useContext, useEffect, useState } from "react";
import { API, get } from "../../config";
import { decrypt, encrypt } from "../Misc";
import { useAppearance } from "./AppearanceContext";

// Create the OrganizationContext
const OrganizationContext = createContext();

// Create the OrganizationProvider component
export function OrganizationProvider({ children }) {
  const token = localStorage.getItem("ts-auth-token");
  const { setAppearance } = useAppearance();

  const [organizationData, setOrganizationData] = useState({
    organizationId: "",
    organizationList: [],
    organizationData: {},
  });

  const apperanceChange = (orgTypeSlug) => {
    if (orgTypeSlug === "real-estate-agent") {
      setAppearance("blue");
    } else if (orgTypeSlug === "auctioneer") {
      setAppearance("trustBlue");
    } else if (orgTypeSlug === "conveyancer" || orgTypeSlug === "lawyer") {
      setAppearance("indigoBlue");
    } else if (orgTypeSlug === "dealer") {
      setAppearance("softBlue");
    } else if (orgTypeSlug === "debt-collector") {
      setAppearance("purple");
    } else if (orgTypeSlug === "general") {
      setAppearance("grey");
    } else {
      setAppearance("blue");
    }
  };

  // Load from localStorage on mount
  useEffect(() => {
    if (localStorage.getItem("organizationData")) {
      const storedUser = decrypt(localStorage.getItem("organizationData"));
      if (storedUser) {
        let data = JSON.parse(storedUser);
        apperanceChange(data?.organizationData?.organization_type?.slug);
        setOrganizationData(JSON.parse(storedUser));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage on change
  useEffect(() => {
    if (organizationData?.organizationId) {
      localStorage.setItem(
        "organizationData",
        encrypt(JSON.stringify(organizationData))
      );
    } else {
      localStorage.removeItem("organizationData");
    }
  }, [organizationData]);

  const fetchOrganizations = async () => {
    try {
      const { data } = await get(`${API.GET_ORGANIZATIONS}`);
      let list = data?.data?.data?.map((item) => {
        return {
          ...item,
          value: item?.id ? item?.id : "",
          label: item?.business_name ? item?.business_name : "",
        };
      });
      setOrganizationData((prev) => {
        return { ...prev, organizationList: list };
      });
    } catch (error) {
      console.error("Error fetching project data:", error);
    }
  };

  useEffect(() => {
    if (organizationData?.organizationId && token) {
      fetchOrganizationData(organizationData?.organizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData?.organizationId, token]);

  const fetchOrganizationData = async (id) => {
    try {
      const { data } = await get(`${API.ORGANIZATION_DETAILS}/` + id);
      apperanceChange(data?.data?.organization_type?.slug);
      setOrganizationData((prev) => {
        return { ...prev, organizationData: data?.data };
      });
    } catch (error) {
      console.error("Error fetching project data:", error);
    }
  };

  // Function to update project data conditionally
  const updateOrganizationData = (newData) => {
    apperanceChange(newData?.organization_type?.slug);
    setOrganizationData((prevData) => {
      return {
        ...prevData,
        organizationData:
          prevData?.organizationData !== newData ? newData : prevData,
      };
    });
  };

  const updateOrganizationId = (value) => {
    setOrganizationData((prev) => {
      return { ...prev, organizationId: value };
    });
  };

  const updateOrganizationList = (list) => {
    if (list?.length > 0) {
      setOrganizationData((prev) => {
        return { ...prev, organizationId: list[0]?.value };
      });
    }
    setOrganizationData((prev) => {
      return { ...prev, organizationList: list };
    });
  };

  // Function to update organization data conditionally
  const fetchOrganizationContext = () => {
    fetchOrganizationData(organizationData?.organizationId);
  };

  const fetchOrganizationListContext = () => {
    fetchOrganizations();
  };

  // Define the context value object
  const contextValue = {
    organizationData,
    setOrganizationData,
    updateOrganizationData,
    fetchOrganizationContext,
    fetchOrganizationListContext,
    updateOrganizationList,
    updateOrganizationId,
  };

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

// Custom hook to use the OrganizationContext
export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within a OrganizationProvider"
    );
  }
  return context;
}
